import * as React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { ScreenName } from '@helpcenter/constants';
import { DocumentTitle } from '@helpcenter/document-title';
import { isIsomorphicDompurifyLibUsed } from '@helpcenter/feature-flags';
import { h500, h600, h700 } from '@helpcenter/styles/src/fonts';
import * as grid from '@helpcenter/styles/src/grid';
import { ThemedLink } from '@helpcenter/themed-link';
import { sanitize } from 'dompurify';
import type { fetchForgotPasswordModel } from 'epics/model/forgot-password';
import type { fetchLoginModel } from 'epics/model/login';
import { sanitize as isomorphicSanitize } from 'isomorphic-dompurify';
import type { WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import type {
	checkLoginActionSuccess,
	exitTestModeAction,
	LoginResponse,
	resetLogin,
} from 'state/actions/login';
import type { LoginUIState } from 'state/ui/login';
import { NoteContent } from 'view/layout/note-content';
import { ScreenErrorBoundary } from 'view/layout/screen-error-boundary';
import { SsoTestModeBanner } from 'view/sso-test-mode-banner';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import Heading from '@atlaskit/heading';
import { Text } from '@atlaskit/primitives';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import { OPERATIONAL_EVENT_TYPE } from '@atlassian/analytics-web-react';
import {
	isDefaultHelpCenter,
	isKoshEnabledForDefaultHelpCenter,
} from '@atlassian/help-center-common-util/advanced-help-center';
import { AnalyticsScreen } from '@atlassian/help-center-common-util/analytics/analytics-screen';
import { UseTypographyMigratedComponent } from '@atlassian/help-center-common-util/enable-typography-migration';
import { getEnv } from '@atlassian/help-center-common-util/env';
import { getBaseName } from '@atlassian/help-center-common-util/history';
import { assign, origin, search } from '@atlassian/help-center-common-util/location';
import {
	AccountStageType,
	AuthMethodType,
	LoginType,
	QUERY_PARAM_SSO_TEST,
} from '@atlassian/help-center-common-util/login/constant';
import { isValidAnnouncement } from '@atlassian/help-center-common-util/login-announcements';
import { RedirectLoggedInUser } from '@atlassian/help-center-common-util/login-redirect';
import {
	getHelpCenterUrl,
	parseLoginSignupRedirectUrlParams,
	getContinueParam,
	getSsoTestModeParamFromUrl,
} from '@atlassian/help-center-common-util/url';
import ThemedBackButton from '../common/themed-back-button';
import { SsoTestModeErrorPage } from './ca-sso-test-mode-error-screen';
import { ForgotPasswordConfirmation } from './forgot-password-confirmation';
import { LoginAnnouncement } from './login-announcements';
import { LoginForm } from './login-form';
import { LoginSingleSignOn } from './login-single-sign-on';
import { LoginSingleSignOnCustomer } from './login-single-sign-on-customer';
import messages from './messages';

export interface LoginProps {
	checkLoginActionSuccess: typeof checkLoginActionSuccess;
	fetchForgotPasswordModel: typeof fetchForgotPasswordModel;
	fetchLoginModel: typeof fetchLoginModel;
	helpCenterName: string;
	isAnonymousEnabled: boolean;
	isLoading: boolean;
	isLoggedIn: boolean;
	loginModel: LoginResponse | undefined;
	loginUIState: LoginUIState;
	onExitTestMode: typeof exitTestModeAction;
	portalId: string | undefined;
	resetLogin: typeof resetLogin;
	resetUrl: string | undefined;
}

/**
 * Check if loginAnnouncement is present
 */
const isAnnouncementPresent = (loginModel: LoginProps['loginModel']): boolean => {
	if (!loginModel) {
		return false;
	}
	const { loginAnnouncementSubject, loginAnnouncementMessage } = loginModel;
	return isValidAnnouncement(loginAnnouncementSubject, loginAnnouncementMessage);
};

const getLoginAnnouncementText = (loginAnnouncementMessage: string) => {
	return (
		<LoginAnnouncementBody
			dangerouslySetInnerHTML={{
				__html: isIsomorphicDompurifyLibUsed()
					? isomorphicSanitize(loginAnnouncementMessage)
					: sanitize(loginAnnouncementMessage),
			}}
		/>
	);
};

export const LoginAnnouncementCard: React.FC<
	Pick<LoginResponse, 'loginAnnouncementSubject' | 'loginAnnouncementMessage'>
> = ({ loginAnnouncementSubject, loginAnnouncementMessage }) => (
	<LoginAnnouncementContainer>
		{loginAnnouncementSubject ? (
			<UseTypographyMigratedComponent
				migratedComponent={<Heading size="small">{loginAnnouncementSubject}</Heading>}
			>
				<LoginAnnouncementTitle>{loginAnnouncementSubject}</LoginAnnouncementTitle>
			</UseTypographyMigratedComponent>
		) : null}
		{loginAnnouncementMessage ? getLoginAnnouncementText(loginAnnouncementMessage) : null}
	</LoginAnnouncementContainer>
);

export class Login extends React.Component<
	LoginProps & WithAnalyticsEventsProps & WrappedComponentProps
> {
	componentDidMount() {
		const { isLoading, resetUrl, loginUIState } = this.props;
		const ssoCustomerMethod = loginUIState.authInfo.authMethods.get(
			AuthMethodType.CUSTOMER_ACCOUNT_SSO,
		);

		if (isLoading) {
			this.props.fetchLoginModel();
		}
		if (!resetUrl) {
			this.props.fetchForgotPasswordModel();
		}
		if (loginUIState.authInfo.authMethods.size === 0) {
			this.fireLoginFailedOperationalEvent({
				noMethodsAvailable: true,
				redirectUrlIsMissing: false,
			});
		} else if (ssoCustomerMethod && ssoCustomerMethod.redirectLink === undefined) {
			this.fireLoginFailedOperationalEvent({
				noMethodsAvailable: false,
				redirectUrlIsMissing: true,
			});
		}
	}

	fireLoginFailedOperationalEvent = (attributes: {
		noMethodsAvailable: boolean;
		redirectUrlIsMissing: boolean;
	}) => {
		const { createAnalyticsEvent } = this.props;
		const { noMethodsAvailable, redirectUrlIsMissing } = attributes;

		if (createAnalyticsEvent) {
			createAnalyticsEvent({
				analyticsType: OPERATIONAL_EVENT_TYPE,
				actionSubject: 'login',
				action: 'failed',
				attributes: { noMethodsAvailable, redirectUrlIsMissing },
			}).fire();
		}
	};

	getJITContinueParam = () => {
		const { destination, isAbsolute } = parseLoginSignupRedirectUrlParams();
		const prefix =
			destination && isAbsolute
				? ''
				: `${origin()}${getBaseName()}/aa-redirect?from=portalLoginJitSignup`;
		return `${prefix}`;
	};

	resetHasLoginPageUpdated = () => {
		this.props.resetLogin(this.props.loginUIState.email);
	};

	renderForgotPasswordConfirmation = () => {
		const { isLoading, loginUIState, helpCenterName, resetUrl } = this.props;

		return (
			<AnalyticsScreen screenName={ScreenName.LOGIN_FORGOT_PASSWORD_CONFIRMATION}>
				<ScreenErrorBoundary screenName={ScreenName.LOGIN_FORGOT_PASSWORD_CONFIRMATION}>
					<NoteContent isLoading={isLoading}>
						<ForgotPasswordConfirmation
							email={loginUIState.email}
							helpCenterName={helpCenterName}
							resetUrl={resetUrl || ''}
							onBackButtonClick={this.resetHasLoginPageUpdated}
						/>
					</NoteContent>
				</ScreenErrorBoundary>
			</AnalyticsScreen>
		);
	};

	renderSsoTestModeErrorPage = () => {
		const { isLoading } = this.props;
		return (
			<AnalyticsScreen screenName={ScreenName.LOGIN_SSO_TEST_MODE_ERROR}>
				<NoteContent isLoading={isLoading}>
					<SsoTestModeErrorPage />
				</NoteContent>
			</AnalyticsScreen>
		);
	};

	renderSingleSignOn = () => {
		const { loginUIState } = this.props;
		const ssoMethod = loginUIState.authInfo.authMethods.get(AuthMethodType.ATLASSIAN_ACCOUNT_SSO);

		if (ssoMethod) {
			const { redirectLink } = ssoMethod;

			const singleSignOnSupplier = () => (
				<AnalyticsScreen screenName={ScreenName.LOGIN_SINGLE_SIGN_ON}>
					<LoginSingleSignOn redirectUrl={redirectLink} email={loginUIState.email} />
				</AnalyticsScreen>
			);

			return this.renderWithTitleAndFooter(singleSignOnSupplier);
		}

		return undefined;
	};

	resetToCustomerPasswordLogin = () => {
		this.props.checkLoginActionSuccess(LoginType.CUSTOMER_ACCOUNT_LOGIN, {
			accountStage: AccountStageType.LOGIN,
			authMethods: new Map([[AuthMethodType.CUSTOMER_ACCOUNT_PASSWORD, {}]]),
		});
	};

	renderSingleSignOnCustomer = () => {
		const { loginUIState } = this.props;
		const ssoMethod = loginUIState.authInfo.authMethods.get(AuthMethodType.CUSTOMER_ACCOUNT_SSO);

		if (ssoMethod) {
			const { redirectLink } = ssoMethod;
			const passwordLoginAvailable = loginUIState.authInfo.authMethods.has(
				AuthMethodType.CUSTOMER_ACCOUNT_PASSWORD,
			);
			const singleSignOnSupplier = () => (
				<AnalyticsScreen screenName={ScreenName.LOGIN_SINGLE_SIGN_ON_CUSTOMER}>
					<LoginSingleSignOnCustomer
						email={loginUIState.email}
						redirectLink={redirectLink}
						passwordLoginAvailable={passwordLoginAvailable}
						onContinueWithPassword={this.resetToCustomerPasswordLogin}
					/>
				</AnalyticsScreen>
			);

			return this.renderWithTitleAndFooter(singleSignOnSupplier);
		}

		return undefined;
	};

	renderLoginPasswordForm = () => {
		return (
			<AnalyticsScreen screenName={ScreenName.LOGIN_PASSWORD}>
				{this.renderLoginForm()}
			</AnalyticsScreen>
		);
	};

	renderLoginForm = () => {
		const { resetUrl, loginUIState, portalId } = this.props;
		const formSupplier = (loginModel: LoginResponse) => (
			<LoginForm
				portalId={portalId}
				contactAdminLink={loginModel.contactAdminLink}
				resetUrl={resetUrl || ''}
				loginUIState={loginUIState}
				canResetPassword={loginModel.canReset}
				continueParam={getContinueParam(portalId)}
			/>
		);

		return this.renderWithTitleAndFooter(formSupplier);
	};

	renderFooter = () => {
		const { intl, isAnonymousEnabled, loginUIState, helpCenterName } = this.props;
		return (
			isAnonymousEnabled &&
			!loginUIState.atlassianAccountCheckPerformed && (
				<FooterContainer>
					<ThemedLink
						to={getHelpCenterUrl()}
						actionSubjectId="helpCenterLink"
						onClick={(e) => {
							// This is ugly but we want to do a full page load (not an SPA load/transition)
							// to allow the backend to make the decision about where we should end up.
							// If CSM DOES NOT allow anonymous access but Jira Admin settings DOES, then
							// an SPA transition will lead to a blank homepage as the backend actually
							// wants to redirect the user to the login page.
							e.preventDefault();
							assign(getEnv().baseUrl);
						}}
					>
						{intl.formatMessage(messages.helpCenterLink, { helpCenterName })}
					</ThemedLink>
				</FooterContainer>
			)
		);
	};

	renderBackButton = () => {
		const { intl } = this.props;
		const buttonText = intl.formatMessage(messages.backButton);

		return (
			<ThemedBackButton
				appearance="link"
				actionSubjectId="returnToLogin"
				onClick={this.resetHasLoginPageUpdated}
			>
				{buttonText}
			</ThemedBackButton>
		);
	};

	getLoginAnnouncementCard = () => {
		const { loginModel } = this.props;
		if (isKoshEnabledForDefaultHelpCenter()) {
			// render new login announcement with relay fetch
			return <LoginAnnouncement />;
		}
		if (loginModel && isAnnouncementPresent(loginModel)) {
			// render old login announcement card with model
			return (
				<LoginAnnouncementCard
					loginAnnouncementSubject={loginModel.loginAnnouncementSubject}
					loginAnnouncementMessage={loginModel.loginAnnouncementMessage}
				/>
			);
		}
		return null;
	};

	// oxlint-disable-next-line no-shadow
	getContent = (componentSupplier: (loginModel: LoginResponse) => JSX.Element) => {
		const { intl, helpCenterName, isLoading, loginModel, loginUIState } = this.props;
		let description = intl.formatMessage(messages.description);

		if (loginUIState.atlassianAccountCheckPerformed) {
			if (loginUIState.authInfo.authMethods.has(AuthMethodType.ATLASSIAN_ACCOUNT_SSO)) {
				switch (loginUIState.authInfo.accountStage) {
					case AccountStageType.SIGNUP:
						description = intl.formatMessage(messages.useAtlassianAccountSignup);
						break;
					case AccountStageType.LOGIN:
					default:
						description = intl.formatMessage(messages.useAtlassianAccountLogin);
						break;
				}
			} else {
				description = intl.formatMessage(messages.descriptionLogin);
			}
		}

		const loginAnnouncementCard = this.getLoginAnnouncementCard();

		if (!isLoading && loginModel) {
			return (
				<>
					{loginUIState.atlassianAccountCheckPerformed && this.renderBackButton()}
					<UseTypographyMigratedComponent
						migratedComponent={<Heading size="large">{helpCenterName}</Heading>}
					>
						<LoginHeader>{helpCenterName}</LoginHeader>
					</UseTypographyMigratedComponent>
					{!loginUIState.atlassianAccountCheckPerformed ? loginAnnouncementCard : null}
					<UseTypographyMigratedComponent
						migratedComponent={<Heading size="medium">{description}</Heading>}
					>
						<LoginDescription>{description}</LoginDescription>
					</UseTypographyMigratedComponent>

					<LoginFormContainer>{componentSupplier(loginModel)}</LoginFormContainer>

					{this.renderFooter()}
				</>
			);
		}

		return <UFOLoadHold name="render-login-page-loading" />;
	};

	renderWithTitleAndFooter = (componentSupplier: (loginModel: LoginResponse) => JSX.Element) => {
		const { isLoading } = this.props;
		const content = this.getContent(componentSupplier);

		const urlParams = new URLSearchParams(search());
		const ssoTest = urlParams.get(QUERY_PARAM_SSO_TEST);

		return (
			<>
				{ssoTest === 'true' ? <SsoTestModeBanner /> : null}
				<NoteContent isLoading={isLoading}>{content}</NoteContent>
			</>
		);
	};

	isInvalidFedRampLogin = () => {
		const isPasswordLoginDisabledByEnv = !isFeatureEnabled(
			'jira-service-management',
			'jsm-help-center-password-login',
		);
		return isPasswordLoginDisabledByEnv && this.noAuthMethodOrOnlyHasPasswordAuthMethod();
	};

	isInvalidIcLogin = () => {
		const isPasswordLoginDisabledByEnv = !isFeatureEnabled(
			'jira-service-management',
			'jsm-help-center-password-login',
		);
		return isPasswordLoginDisabledByEnv && this.noAuthMethodOrOnlyHasPasswordAuthMethod();
	};

	noAuthMethodOrOnlyHasPasswordAuthMethod = () => {
		const { loginUIState } = this.props;

		return (
			loginUIState.authInfo.authMethods.size === 0 ||
			(loginUIState.authInfo.authMethods.size === 1 &&
				loginUIState.authInfo.authMethods.has(AuthMethodType.CUSTOMER_ACCOUNT_PASSWORD))
		);
	};

	renderNoLoginMethodPage = () => {
		const { intl, helpCenterName, isLoading } = this.props;

		return (
			<NoteContent isLoading={isLoading}>
				{this.renderBackButton()}
				<UseTypographyMigratedComponent
					migratedComponent={<Heading size="large">{helpCenterName}</Heading>}
				>
					<LoginHeader>{helpCenterName}</LoginHeader>
				</UseTypographyMigratedComponent>
				<UseTypographyMigratedComponent
					migratedComponent={
						<Text>{intl.formatMessage(messages.noLoginMethodWarningContent)}</Text>
					}
				>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<p>{intl.formatMessage(messages.noLoginMethodWarningContent)}</p>
				</UseTypographyMigratedComponent>
			</NoteContent>
		);
	};

	renderLoginPage = () => {
		const { intl, loginUIState } = this.props;
		let loginPage;

		if (loginUIState.showPasswordConfirmation) {
			loginPage = this.renderForgotPasswordConfirmation();
		} else if (loginUIState.authInfo.authMethods.has(AuthMethodType.CUSTOMER_ACCOUNT_SSO)) {
			loginPage = this.renderSingleSignOnCustomer();
		} else if (loginUIState.authInfo.authMethods.has(AuthMethodType.ATLASSIAN_ACCOUNT_SSO)) {
			loginPage = this.renderSingleSignOn();
		} else if (loginUIState.showSsoTestModeErrorPage) {
			loginPage = this.renderSsoTestModeErrorPage();
		} else if (loginUIState.atlassianAccountCheckPerformed) {
			if (this.isInvalidFedRampLogin() || this.isInvalidIcLogin()) {
				loginPage = this.renderNoLoginMethodPage();
			} else {
				loginPage = this.renderLoginPasswordForm();
			}
		} else {
			loginPage = this.renderLoginForm();
		}

		return (
			<DocumentTitle title={intl.formatMessage(messages.documentTitle)}>{loginPage}</DocumentTitle>
		);
	};

	getNavigationTiming(): PerformanceNavigationTiming | undefined {
		if (__SERVER__) {
			return undefined;
		}
		return globalThis.performance?.getEntriesByType?.('navigation')?.[0] as
			| PerformanceNavigationTiming
			| undefined;
	}

	render() {
		const { isLoggedIn, portalId } = this.props;

		const navigation = this.getNavigationTiming();

		if (isLoggedIn) {
			return <RedirectLoggedInUser portalId={portalId} />;
		}

		return (
			<AnalyticsContext
				data={{
					attributes: { isSsoTestMode: getSsoTestModeParamFromUrl(new URLSearchParams(search())) },
				}}
			>
				<UFOCustomData
					data={{
						isFromRedirect: navigation ? navigation.redirectCount > 0 : undefined,
						redirectCount: navigation ? navigation.redirectCount : undefined,
						isDefaultHelpCenter: isDefaultHelpCenter(),
					}}
				/>
				<div data-test-id="login-page.wrapper">{this.renderLoginPage()}</div>
			</AnalyticsContext>
		);
	}
}

export default injectIntl(Login);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const LoginDescription = styled.h2`
	${h600};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const LoginFormContainer = styled.div`
	margin-top: ${token('space.300', '24px')};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const FooterContainer = styled.div`
	margin-top: ${token('space.400', '32px')};
	text-align: center;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const LoginAnnouncementContainer = styled.div`
	padding-bottom: ${token('space.200', '16px')};
	border-bottom: ${token('border.width', '1px')} solid
		${token('color.border', 'rgba(9, 30, 66, 0.14)')};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const LoginAnnouncementTitle = styled.div`
	${h500};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const LoginAnnouncementBody = styled.div`
	margin-top: ${token('space.100', '8px')};
	max-width: ${grid.multiple(42).px};
	overflow-wrap: break-word;
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const LoginHeader = styled.h1`
	${h700};
`;
